/**
 * AppBlockEditOverlays — Phase 2.2 (Plan §8) App.tsx slice #6.
 *
 * Bundles the 3 remaining block-edit/test inline mounts:
 *   - DYN-2 B.2 LookupTableDialog (BLOOKUPTABLE) — ~42 LOC
 *   - DYN-2 B.2 BlockPropertiesTableDialog (BTABLE) — ~39 LOC
 *   - DYN-1.9 Test Block banner — ~47 LOC
 *
 * All three render only inside block-edit / block-test sessions.
 */

import { Suspense, type ComponentType } from "react";

import type {
  DrawingDocument,
  EditorCommand,
} from "@dxf-ai-editor/drawing-model";

interface LookupTableDialogState {
  blockName: string;
  tableId: string;
}

interface AppBlockEditOverlaysProps {
  geometryDocument: DrawingDocument;
  lookupTableDialog: LookupTableDialogState | null;
  setLookupTableDialog: (next: LookupTableDialogState | null) => void;
  blockPropertiesDialogOpen: boolean;
  setBlockPropertiesDialogOpen: (open: boolean) => void;
  dispatch: (command: EditorCommand) => void;
  appendCommandFeed: (text: string) => void;
  t: (key: string, params?: Record<string, string>) => string;
  LookupTableDialog: ComponentType<Record<string, unknown>>;
  BlockPropertiesTableDialog: ComponentType<Record<string, unknown>>;
}

export function AppBlockEditOverlays({
  geometryDocument,
  lookupTableDialog,
  setLookupTableDialog,
  blockPropertiesDialogOpen,
  setBlockPropertiesDialogOpen,
  dispatch,
  appendCommandFeed,
  t,
  LookupTableDialog,
  BlockPropertiesTableDialog,
}: AppBlockEditOverlaysProps): JSX.Element {
  return (
    <>
      {/* ── DYN-2 B.2 Lookup Table editor (BLOOKUPTABLE) ── */}
      {(() => {
        if (!lookupTableDialog) return null;
        const { blockName, tableId } = lookupTableDialog;
        const blockDef = geometryDocument.blocks[blockName];
        const metadata = blockDef?.dynamicMetadata;
        const table = metadata?.lookupTables.find((tt) => tt.id === tableId);
        if (!blockDef || !metadata || !table) {
          return null;
        }
        const others = metadata.lookupTables
          .filter((tt) => tt.id !== tableId)
          .map((tt) => tt.name);
        return (
          <Suspense fallback={null}>
            <LookupTableDialog
              blockName={blockName}
              table={table}
              parameters={metadata.parameters}
              existingTableNames={others}
              onSave={(next: {
                id: string;
                name: string;
                inputColumns: unknown;
                outputColumn: unknown;
                rows: unknown;
              }) => {
                dispatch({
                  type: "geometry/update-lookup-table",
                  source: "toolbar",
                  blockName,
                  tableId: next.id,
                  updates: {
                    name: next.name,
                    inputColumns: next.inputColumns,
                    outputColumn: next.outputColumn,
                    rows: next.rows,
                  },
                } as EditorCommand);
                appendCommandFeed(
                  t("summary.lookup_table_saved", {
                    name: next.name || next.id,
                  }),
                );
              }}
              onCancel={() => setLookupTableDialog(null)}
            />
          </Suspense>
        );
      })()}

      {/* ── DYN-2 B.2 Block Properties Table editor (BTABLE) ── */}
      {(() => {
        if (!blockPropertiesDialogOpen) return null;
        const activeGeoSpace = geometryDocument.spaces.find(
          (s) => s.id === geometryDocument.activeSpaceId,
        );
        if (
          !activeGeoSpace ||
          activeGeoSpace.kind !== "block-edit" ||
          !activeGeoSpace.blockEditName
        ) {
          return null;
        }
        const blockName = activeGeoSpace.blockEditName;
        const blockDef = geometryDocument.blocks[blockName];
        if (!blockDef) return null;
        const existingTable = blockDef.dynamicMetadata
          ?.blockPropertiesTable ?? {
          entries: [],
        };
        return (
          <Suspense fallback={null}>
            <BlockPropertiesTableDialog
              blockName={blockName}
              table={existingTable}
              onSave={(next: unknown) => {
                dispatch({
                  type: "geometry/set-block-properties-table",
                  source: "toolbar",
                  blockName,
                  table: next,
                } as EditorCommand);
                appendCommandFeed(
                  t("summary.block_properties_saved", { block: blockName }),
                );
              }}
              onCancel={() => setBlockPropertiesDialogOpen(false)}
            />
          </Suspense>
        );
      })()}

      {/* ── DYN-1.9 Test Block banner + Close Test button ── */}
      {(() => {
        const activeGeoSpace = geometryDocument.spaces.find(
          (s) => s.id === geometryDocument.activeSpaceId,
        );
        if (!activeGeoSpace || activeGeoSpace.kind !== "block-test") {
          return null;
        }
        const previewName = activeGeoSpace.blockEditName ?? "";
        return (
          <div
            className="block-test-banner"
            data-testid="block-test-banner"
            style={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              padding: "6px 14px",
              background: "#2b5c26",
              color: "#e8f5e9",
              border: "1px solid #4a8a42",
              borderRadius: "6px",
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: 12,
              boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
            }}
          >
            <span>{t("banner.testblock.title", { name: previewName })}</span>
            <button
              type="button"
              className="dialog-btn"
              onClick={() => {
                dispatch({
                  type: "space/exit-block-test",
                  source: "toolbar",
                } as EditorCommand);
                appendCommandFeed(t("summary.testblock_exited"));
              }}
              data-testid="banner-exit-test-block-button"
            >
              {t("banner.testblock.exit")}
            </button>
          </div>
        );
      })()}
    </>
  );
}
